/**
 * Health Hero — the expanded summary view.
 * Left: large gauge showing N/total count.
 * Right: tile grid of headline counts (green, amber, red, not yet reliable, n/a).
 * Appears when Summary toggle is set to "Health hero".
 *
 * "Not yet reliable" (summary.insufficient) is the umbrella for the three
 * gated-out neutral statuses — rendered in the same neutral grey family as
 * n/a but as a distinct tile so it isn't mistaken for red/off-track.
 */
import { Box, Grid, Stack, Typography } from '@mui/material';
import type { ReactElement } from 'react';

import type { ScorecardSummary } from '@/types/scorecard';

interface HealthHeroProps {
  summary: ScorecardSummary;
  /** Called when a tile is clicked — used to filter the card view. */
  onStatusClick?: (status: 'green' | 'amber' | 'red' | 'insufficient' | 'na') => void;
}

const TILE_CONFIG = [
  { key: 'green' as const, label: 'On track', color: '#6BFFAB', bg: 'rgba(107,255,171,0.08)' },
  { key: 'amber' as const, label: 'At risk', color: '#FFB000', bg: 'rgba(255,176,0,0.08)' },
  { key: 'red' as const, label: 'Off track', color: '#ef4444', bg: 'rgba(239,68,68,0.08)' },
  { key: 'insufficient' as const, label: 'Not yet reliable', color: '#8a8a94', bg: 'rgba(138,138,148,0.08)' },
  { key: 'na' as const, label: 'N/A', color: '#6c6c78', bg: 'rgba(108,108,120,0.08)' },
];

export function HealthHero({ summary, onStatusClick }: HealthHeroProps): ReactElement {
  const healthyCount = summary.green;
  const total = summary.total;

  return (
    <Grid container spacing={2} sx={{ mb: 2.25 }} role="status" aria-label="Health overview">
      {/* Gauge */}
      <Grid item xs={12} sm={4} md={3}>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '8px',
            background: 'radial-gradient(120% 120% at 50% 0, #1a1a21, #15151b)',
            p: 2.75,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            height: '100%',
            minHeight: 180,
          }}
        >
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.625rem',
              letterSpacing: '0.7px',
              textTransform: 'uppercase',
              color: 'text.disabled',
              mb: 1,
            }}
          >
            Metrics healthy
          </Typography>

          <Box aria-label={`${healthyCount} of ${total} metrics on track`}>
            <Typography
              component="span"
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '3.2rem',
                fontWeight: 700,
                lineHeight: 1,
                color: healthyCount === total ? '#6BFFAB' : healthyCount > total * 0.7 ? '#FFB000' : '#ef4444',
              }}
            >
              {healthyCount}
            </Typography>
            <Typography
              component="span"
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '1.35rem',
                color: 'text.disabled',
                lineHeight: 1,
              }}
            >
              /{total}
            </Typography>
          </Box>

          <Typography
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.6875rem',
              letterSpacing: '0.4px',
              mt: 1.375,
              color:
                summary.red > 0
                  ? '#ef4444'
                  : summary.amber > 0
                  ? '#FFB000'
                  : '#6BFFAB',
            }}
          >
            {summary.verdict}
          </Typography>

          {/* Mini count row */}
          <Stack direction="row" spacing={1.625} sx={{ mt: 1.75 }}>
            {TILE_CONFIG.map(({ key, color }) => (
              <Stack key={key} direction="row" alignItems="center" spacing={0.75}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: color,
                    flexShrink: 0,
                  }}
                  aria-hidden="true"
                />
                <Typography
                  sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: '0.6875rem',
                    color: 'text.secondary',
                  }}
                >
                  {summary[key]}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      </Grid>

      {/* Tile grid */}
      <Grid item xs={12} sm={8} md={9}>
        <Grid container spacing={1.75} sx={{ height: '100%' }}>
          {TILE_CONFIG.map(({ key, label, color, bg }) => (
            <Grid item xs={6} sm={4} key={key}>
              <Box
                onClick={() => onStatusClick?.(key)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onStatusClick?.(key); }}
                aria-label={`${label}: ${summary[key]} metrics`}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '6px',
                  background: 'background.elev2',
                  p: 1.875,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.625,
                  cursor: 'pointer',
                  minHeight: 44,
                  transition: 'transform 120ms, border-color 160ms',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    borderColor: '#3a3a44',
                    background: bg,
                  },
                  '&:focus-visible': {
                    outline: `2px solid ${color}`,
                    outlineOffset: 2,
                  },
                }}
              >
                {/* Tile name + color dot */}
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: color,
                      boxShadow: `0 0 5px ${color}70`,
                      flexShrink: 0,
                    }}
                    aria-hidden="true"
                  />
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: 'text.secondary',
                    }}
                  >
                    {label}
                  </Typography>
                </Stack>

                {/* Big number */}
                <Typography
                  sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: '1.75rem',
                    fontWeight: 700,
                    lineHeight: 1.05,
                    color,
                  }}
                >
                  {summary[key]}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );
}
